/* ==========================================================================
   anime-pass-product-reviews
   Native reviews widget. Reads a JSON file from the theme assets (see the
   contract in sections/anime-pass-product-reviews.liquid), renders lazily,
   and uses textContent only so review text can never inject markup.
   ========================================================================== */
(function () {
  'use strict';

  var INIT_FLAG = 'aprInit';

  /* ---------- small DOM helpers ---------- */
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }
  function fill(tpl, map) {
    return String(tpl).replace(/\[(\w+)\]/g, function (m, k) {
      return map[k] != null ? map[k] : m;
    });
  }

  /* ---------- locale helpers ---------- */
  function norm(code) {
    return String(code || '').toLowerCase();
  }
  // candidate lookup order for a storefront locale, e.g. "pt-BR" -> ["pt-br","pt"]
  function candidates(locale) {
    var l = norm(locale);
    var out = [l];
    if (l.indexOf('-') > -1) out.push(l.split('-')[0]);
    return out;
  }
  // find an object key matching one of the candidate locales, case-insensitively
  function matchKey(obj, cands) {
    if (!obj) return null;
    var keys = Object.keys(obj);
    for (var c = 0; c < cands.length; c++) {
      for (var i = 0; i < keys.length; i++) {
        if (norm(keys[i]) === cands[c]) return keys[i];
      }
    }
    return null;
  }
  // choose the text to display and whether it is a translation of the original
  // Decode the entities the data files / submission encoder use. ONE layer, with
  // &amp; LAST so an authored literal like "&lt;" survives (it is stored as
  // "&amp;lt;" and must decode back to "&lt;", not "<"). Rendering is textContent,
  // so decoded text is never treated as markup.
  function decodeEntities(s) {
    return String(s == null ? '' : s)
      .replace(/&#39;/g, "'").replace(/&quot;/g, '"')
      .replace(/&lt;/g, '<').replace(/&gt;/g, '>')
      .replace(/&#123;/g, '{').replace(/&#125;/g, '}').replace(/&#37;/g, '%')
      .replace(/&amp;/g, '&');
  }

  function pickText(obj, locale, originalLocale) {
    if (!obj || typeof obj !== 'object') return null;
    var key = matchKey(obj, candidates(locale));
    if (key != null) {
      return {
        text: decodeEntities(obj[key]),
        isTranslation: norm(key) !== norm(originalLocale),
        originalText: null
      };
    }
    var ok = matchKey(obj, candidates(originalLocale));
    if (ok != null) return { text: decodeEntities(obj[ok]), isTranslation: false, originalText: null };
    var all = Object.keys(obj);
    return all.length ? { text: decodeEntities(obj[all[0]]), isTranslation: false, originalText: null } : null;
  }

  function languageName(code, inLocale) {
    var base = norm(code).split('-')[0];
    try {
      if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
        var dn = new Intl.DisplayNames([inLocale || 'en'], { type: 'language' });
        var name = dn.of(base);
        if (name) return name;
      }
    } catch (e) {}
    return code;
  }

  function fmtDate(iso, locale) {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return iso || '';
    try {
      return new Intl.DateTimeFormat(locale, {
        year: 'numeric', month: 'short', day: 'numeric'
      }).format(d);
    } catch (e) {
      return d.toISOString().slice(0, 10);
    }
  }

  function fmtAvg(n, locale) {
    try {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 1, maximumFractionDigits: 1
      }).format(n);
    } catch (e) {
      return n.toFixed(1);
    }
  }

  function pluralForm(n, locale, strings) {
    var cat = 'other';
    try {
      if (typeof Intl !== 'undefined' && Intl.PluralRules) {
        cat = new Intl.PluralRules(locale).select(n);
      } else {
        cat = n === 1 ? 'one' : 'other';
      }
    } catch (e) {
      cat = n === 1 ? 'one' : 'other';
    }
    if (cat === 'one' && strings.countOne) return strings.countOne;
    return strings.countOther || strings.countOne || '[count]';
  }

  /* ---------- rendering ---------- */
  function starRow(rating, strings, cls) {
    var wrap = el('span', 'apr-stars' + (cls ? ' ' + cls : ''));
    var glyphs = el('span', 'apr-stars-glyphs');
    glyphs.setAttribute('aria-hidden', 'true');
    var r = Math.max(0, Math.min(5, Math.round(rating)));
    for (var i = 1; i <= 5; i++) {
      glyphs.appendChild(el('span', 'apr-star' + (i <= r ? ' is-on' : ''), '★'));
    }
    wrap.appendChild(glyphs);
    var sr = el('span', 'apr-visually-hidden', fill(strings.ratingStars, { rating: rating }));
    wrap.appendChild(sr);
    return wrap;
  }

  function reviewCard(review, cfg) {
    var s = cfg.strings;
    var li = el('li', 'apr-card');

    var head = el('div', 'apr-card-head');
    head.appendChild(starRow(review.rating, s));

    var meta = el('div', 'apr-card-meta');
    meta.appendChild(el('span', 'apr-author', review.author || ''));
    if (review.date) {
      var time = el('time', 'apr-date', fmtDate(review.date, cfg.locale));
      time.setAttribute('datetime', String(review.date));
      meta.appendChild(time);
    }
    head.appendChild(meta);

    var badges = el('div', 'apr-badges');
    if (review.verified === true) badges.appendChild(el('span', 'apr-badge apr-badge-verified', s.verified));
    if (cfg.showBadges && review.source) {
      if (norm(review.source) === 'etsy') {
        badges.appendChild(el('span', 'apr-badge apr-badge-source', s.sourceEtsy));
      }
    }
    if (badges.childNodes.length) head.appendChild(badges);
    li.appendChild(head);

    if (review.title) {
      var tp = pickText(review.title, cfg.locale, review.original_locale);
      if (tp && tp.text) li.appendChild(el('h3', 'apr-card-title', tp.text));
    }

    var bp = pickText(review.body, cfg.locale, review.original_locale);
    if (bp && bp.text != null) {
      var body = el('p', 'apr-card-body', bp.text);
      li.appendChild(body);

      if (bp.isTranslation) {
        var orig = pickText(review.body, review.original_locale, review.original_locale);
        var toggleWrap = el('div', 'apr-translated');
        var langName = languageName(review.original_locale, cfg.locale);
        // Prefer a locale-provided, fully pre-elided phrase per source language
        // (French needs "de l'anglais" vs "du portugais" — a single interpolated
        // template can't produce correct grammar). Falls back to the generic
        // "translated from [language]" template when no per-language phrase exists.
        var byLang = s.translatedFromByLang && s.translatedFromByLang[review.original_locale];
        // Reject empty values and Shopify's "Translation missing: ..." sentinel
        // (case-insensitive — Shopify emits a capital "T") so a locale without a
        // per-language phrase never leaks the raw key to customers.
        if (byLang && (!byLang.length || byLang.toLowerCase().indexOf('translation missing') !== -1)) byLang = null;
        // Fall back to the generic, language-free label ("Translated") rather than
        // an interpolated name, so a missing phrase degrades cleanly in-locale.
        var fallback = s.translatedGeneric;
        if (!fallback || fallback.toLowerCase().indexOf('translation missing') !== -1) {
          fallback = fill(s.translatedFrom, { language: langName });
        }
        var noteText = byLang ? byLang : fallback;
        toggleWrap.appendChild(el('span', 'apr-translated-note', noteText + ' · '));
        var btn = el('button', 'apr-show-original', s.showOriginal);
        btn.type = 'button';
        btn.addEventListener('click', function () {
          if (orig && orig.text != null) body.textContent = orig.text;
          if (toggleWrap.parentNode) toggleWrap.parentNode.removeChild(toggleWrap);
        });
        toggleWrap.appendChild(btn);
        li.appendChild(toggleWrap);
      }
    }

    if (review.reply) {
      var rp = pickText(review.reply, cfg.locale, review.original_locale);
      if (rp && rp.text) {
        var reply = el('div', 'apr-reply');
        reply.appendChild(el('span', 'apr-reply-label', s.ownerReply));
        reply.appendChild(el('p', 'apr-reply-body', rp.text));
        li.appendChild(reply);
      }
    }

    return li;
  }

  function buildSummary(container, reviews, cfg) {
    clear(container);
    var s = cfg.strings;
    var total = reviews.length;
    var sum = 0, dist = [0, 0, 0, 0, 0];
    for (var i = 0; i < total; i++) {
      var r = Math.max(1, Math.min(5, Math.round(reviews[i].rating)));
      sum += r;
      dist[r - 1]++;
    }
    var avg = total ? sum / total : 0;

    var top = el('div', 'apr-summary-top');
    var avgWrap = el('div', 'apr-avg');
    avgWrap.appendChild(el('span', 'apr-avg-num', fmtAvg(avg, cfg.locale)));
    avgWrap.setAttribute('aria-label', fill(s.averageAria, { rating: fmtAvg(avg, cfg.locale) }));
    var avgRight = el('div', 'apr-avg-right');
    avgRight.appendChild(starRow(avg, s, 'apr-stars-lg'));
    avgRight.appendChild(el('span', 'apr-count', fill(pluralForm(total, cfg.locale, s), { count: total })));
    top.appendChild(avgWrap);
    top.appendChild(avgRight);
    container.appendChild(top);

    if (cfg.showHistogram && total) {
      var hist = el('div', 'apr-histogram');
      for (var star = 5; star >= 1; star--) {
        var count = dist[star - 1];
        var pct = total ? Math.round((count / total) * 100) : 0;
        var row = el('div', 'apr-hist-row');
        row.setAttribute('aria-label', fill(s.histogramRow, { star: star, count: count }));
        row.appendChild(el('span', 'apr-hist-star', star + '★'));
        var track = el('span', 'apr-hist-track');
        var barFill = el('span', 'apr-hist-fill');
        barFill.style.width = pct + '%';
        track.appendChild(barFill);
        row.appendChild(track);
        row.appendChild(el('span', 'apr-hist-count', String(count)));
        hist.appendChild(row);
      }
      container.appendChild(hist);
    }
    container.hidden = false;
  }

  function sortReviews(list, how) {
    var arr = list.slice();
    function dateVal(r) { var d = new Date(r.date); return isNaN(d.getTime()) ? 0 : d.getTime(); }
    arr.sort(function (a, b) {
      if (how === 'highest') return (b.rating - a.rating) || (dateVal(b) - dateVal(a));
      if (how === 'lowest') return (a.rating - b.rating) || (dateVal(b) - dateVal(a));
      return dateVal(b) - dateVal(a); // newest
    });
    return arr;
  }

  function emitJsonLd(root, reviews, cfg) {
    // never in "all" mode; guard already set emitJsonLd false there
    var prev = root.querySelector('[data-apr-ld]');
    if (prev) prev.parentNode.removeChild(prev);
    if (!cfg.emitJsonLd || !reviews.length) return;
    var sum = 0;
    for (var i = 0; i < reviews.length; i++) sum += Math.max(1, Math.min(5, Math.round(reviews[i].rating)));
    var avg = sum / reviews.length;
    var sorted = sortReviews(reviews, 'newest').slice(0, 10);
    var node = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      '@id': cfg.productId,
      'aggregateRating': {
        '@type': 'AggregateRating',
        'ratingValue': avg.toFixed(1),
        'reviewCount': reviews.length,
        'bestRating': '5',
        'worstRating': '1'
      },
      'review': sorted.map(function (r) {
        var bp = pickText(r.body, r.original_locale, r.original_locale);
        return {
          '@type': 'Review',
          'author': { '@type': 'Person', 'name': r.author || '' },
          'datePublished': r.date || '',
          'reviewRating': { '@type': 'Rating', 'ratingValue': String(r.rating), 'bestRating': '5', 'worstRating': '1' },
          'reviewBody': (bp && bp.text) || ''
        };
      })
    };
    if (cfg.productName) node.name = cfg.productName;
    var script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-apr-ld', '');
    script.textContent = JSON.stringify(node);
    root.appendChild(script);
  }

  /* ---------- per-section controller ---------- */
  function setup(root) {
    if (root.dataset[INIT_FLAG] === '1') return;
    root.dataset[INIT_FLAG] = '1';

    var cfgEl = root.querySelector('[data-apr-config]');
    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    cfg.strings = cfg.strings || {};

    var wrapper = root.parentNode;
    var sentinel = wrapper ? wrapper.querySelector('[data-apr-sentinel]') : null;

    var summaryEl = root.querySelector('[data-apr-summary]');
    var toolbarEl = root.querySelector('[data-apr-toolbar]');
    var listEl = root.querySelector('[data-apr-list]');
    var moreEl = root.querySelector('[data-apr-more]');
    var placeholderEl = root.querySelector('[data-apr-placeholder]');
    var sortSel = root.querySelector('[data-apr-sort]');
    var writeBtn = root.querySelector('[data-apr-write]');
    var emptyEl = root.querySelector('[data-apr-empty]');
    var modal = root.querySelector('[data-apr-modal]');

    var state = { all: [], sorted: [], rendered: 0 };
    var loaded = false;

    function showWriteButton() { if (cfg.submissionsEnabled && writeBtn) writeBtn.hidden = false; }

    function showPlaceholder(kind) {
      // editor only — explain why nothing rendered (distinguish the causes)
      var map = { file: cfg.fileUrl, handle: cfg.handle || '(none)' };
      var s = cfg.strings, tpl;
      if (kind === 'empty-index') tpl = s.editorEmptyIndex;        // liquid: index renders no parts
      else if (kind === 'no-match') tpl = s.editorNoMatch || s.editorNoReviews; // liquid: data exists, none for handle
      else if (kind === 'no-reviews') tpl = s.editorNoReviews;     // json: file ok, none for handle
      else tpl = s.editorNoFile;                                   // json: fetch failed
      placeholderEl.textContent = fill(tpl || '', map);
      placeholderEl.hidden = false;
      root.hidden = false;
      root.classList.add('is-editor-empty');
    }

    function renderBatch() {
      var next = state.sorted.slice(state.rendered, state.rendered + (state.rendered === 0 ? cfg.initial : cfg.batch));
      var frag = document.createDocumentFragment();
      for (var i = 0; i < next.length; i++) frag.appendChild(reviewCard(next[i], cfg));
      listEl.appendChild(frag);
      state.rendered += next.length;
      moreEl.hidden = state.rendered >= state.sorted.length;
    }

    function rerender() {
      clear(listEl);
      state.rendered = 0;
      state.sorted = sortReviews(state.all, sortSel ? sortSel.value : 'newest');
      renderBatch();
    }

    // Render an already-filtered, flat array of review objects.
    function render(reviews, emptyKind) {
      reviews = (reviews || []).filter(function (r) { return r && typeof r.rating === 'number'; });
      if (!reviews.length) {
        if (cfg.designMode) showPlaceholder(emptyKind || 'no-reviews');
        // Empty state: heading + "no reviews yet" line + Write a review button.
        if (cfg.designMode || cfg.showWhenEmpty) {
          root.hidden = false;
          if (emptyEl) emptyEl.hidden = false;
          showWriteButton();
        }
        return; // else storefront stays hidden
      }
      state.all = reviews;
      root.hidden = false;
      if (emptyEl) emptyEl.hidden = true;
      buildSummary(summaryEl, reviews, cfg);
      toolbarEl.hidden = false;
      rerender();
      showWriteButton();
      emitJsonLd(root, reviews, cfg); // no-op unless cfg.emitJsonLd (json source only)
    }

    // JSON source: select the current product's array (or merge all), then render.
    function onData(data) {
      var reviews = [];
      if (data && data.reviews) {
        if (cfg.mode === 'all') {
          Object.keys(data.reviews).forEach(function (h) {
            if (Array.isArray(data.reviews[h])) reviews = reviews.concat(data.reviews[h]);
          });
        } else if (Array.isArray(data.reviews[cfg.handle])) {
          reviews = data.reviews[cfg.handle].slice();
        }
      }
      render(reviews, 'no-reviews');
    }

    function onError() {
      if (cfg.designMode) showPlaceholder('no-file');
      // storefront: stay hidden
    }

    function start() {
      if (loaded) return;
      loaded = true;
      if (cfg.dataSource === 'liquid') {
        // Reviews are already filtered + inlined by Liquid — no network fetch.
        var el = root.querySelector('[data-apr-inline]');
        var arr = [];
        if (el) { try { arr = JSON.parse(el.textContent) || []; } catch (e) { arr = []; } }
        var emptyKind = (cfg.hasAnyData && cfg.hasAnyData > 0) ? 'no-match' : 'empty-index';
        render(arr, emptyKind);
      } else {
        fetch(cfg.fileUrl, { headers: { Accept: 'application/json' } })
          .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.json(); })
          .then(function (data) { onData(data); })
          .catch(function () { onError(); });
      }
    }

    // delegated / direct listeners
    if (sortSel) sortSel.addEventListener('change', function () { if (state.all.length) rerender(); });
    if (moreEl) moreEl.addEventListener('click', function () { renderBatch(); });

    /* ---------- Write-a-review modal ---------- */
    var lastFocus = null;
    function focusables() {
      return Array.prototype.filter.call(
        modal.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),textarea:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])'),
        function (el) { return el.offsetWidth || el.offsetHeight || el === document.activeElement; }
      );
    }
    function openModal() {
      if (!modal) return;
      lastFocus = document.activeElement;
      if (typeof modal.showModal === 'function') {
        if (!modal.open) modal.showModal();
      } else {
        modal.setAttribute('open', ''); modal.setAttribute('role', 'dialog'); modal.setAttribute('aria-modal', 'true');
      }
      try { document.documentElement.style.overflow = 'hidden'; } catch (e) {}
      var f = focusables()[0];
      if (f) f.focus();
    }
    function closeModal() {
      if (!modal) return;
      if (typeof modal.close === 'function' && modal.open) { modal.close(); }
      else { modal.removeAttribute('open'); restoreAfterClose(); }
    }
    function restoreAfterClose() {
      try { document.documentElement.style.overflow = ''; } catch (e) {}
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
    if (modal) {
      if (writeBtn) writeBtn.addEventListener('click', openModal);
      modal.addEventListener('close', restoreAfterClose);               // native dialog (Esc or close())
      modal.addEventListener('click', function (e) { if (e.target === modal) closeModal(); }); // backdrop
      modal.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { e.preventDefault(); closeModal(); return; }
        if (e.key !== 'Tab') return;
        var fs = focusables(); if (!fs.length) return;
        var first = fs[0], last = fs[fs.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });
      Array.prototype.forEach.call(modal.querySelectorAll('[data-apr-close]'), function (b) {
        b.addEventListener('click', closeModal);
      });

      // On submit (normal POST): prefix the body, build the ready-to-paste data line.
      var form = modal.querySelector('form');
      if (form) {
        form.addEventListener('submit', function () {
          var bodyEl = form.querySelector('[data-apr-body]');
          var dl = form.querySelector('[data-apr-dataline]');
          if (dl) dl.value = buildDataLine(form, bodyEl ? bodyEl.value : '');
          var PREFIX = '[Product review] ';
          if (bodyEl && bodyEl.value.indexOf(PREFIX) !== 0) bodyEl.value = PREFIX + bodyEl.value;
          // no preventDefault — let Shopify handle the POST + spam protection
        });
      }
    }

    function encodeLiquid(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;')
        .replace(/\{/g, '&#123;').replace(/\}/g, '&#125;').replace(/%/g, '&#37;')
        .replace(/\s+/g, ' ').trim();
    }
    function mapLang(loc) {
      var l = String(loc || 'en').toLowerCase();
      if (l === 'pt-br') return 'pt-BR';
      if (l === 'zh-cn') return 'zh-CN';
      return l.split('-')[0];
    }
    function buildDataLine(form, rawBody) {
      function val(name) { var el = form.querySelector('[name="contact[' + name + ']"]'); return el ? el.value : ''; }
      var rating = (form.querySelector('[name="contact[Rating]"]:checked') || {}).value || '0';
      var title = val('Review title');
      var handleEl = form.querySelector('[data-apr-handle]');
      var handle = (handleEl && handleEl.value) || cfg.handle || '';
      var localeEl = form.querySelector('[data-apr-locale]');
      var lang = mapLang((localeEl && localeEl.value) || cfg.locale || 'en');
      var date = new Date().toISOString().slice(0, 10);
      var line = "{% render 'anime-pass-product-review', author: '" + encodeLiquid(val('name')) +
        "', rating: " + rating;
      if (title && title.trim()) line += ", title: '" + encodeLiquid(title) + "'";
      line += ", body: '" + encodeLiquid(rawBody) + "', date: '" + date +
        "', product_url: '/products/" + handle + "', verified: false, source: 'store', lang: '" + lang + "' %}";
      return line;
    }

    // ---- lazy trigger ----
    // IntersectionObserver is the primary (generous rootMargin), but its initial
    // callback is not reliably delivered for a target that is already on screen,
    // so we also do a deterministic rect check now and on scroll/resize. load()
    // is idempotent (the `loaded` guard), so overlapping triggers are harmless.
    var io = null;
    var MARGIN = 600;
    function near() {
      var r = sentinel.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      return r.top <= vh + MARGIN && r.bottom >= -MARGIN;
    }
    function trigger() {
      if (io) { io.disconnect(); io = null; }
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      start();
    }
    function onScroll() { if (near()) trigger(); }

    if (cfg.designMode) {
      // editor: start immediately so the placeholder / content is visible
      start();
    } else if (sentinel) {
      if (near()) {
        trigger();
      } else {
        if ('IntersectionObserver' in window) {
          io = new IntersectionObserver(function (entries) {
            for (var i = 0; i < entries.length; i++) {
              if (entries[i].isIntersecting) { trigger(); break; }
            }
          }, { rootMargin: MARGIN + 'px 0px' });
          io.observe(sentinel);
        }
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
      }
    } else {
      start();
    }

    // If a submission just came back (success or validation errors), reveal the
    // section and open the modal immediately (don't wait for scroll).
    if (modal && (modal.querySelector('[data-apr-thankyou]') || modal.querySelector('[data-apr-form-errors]'))) {
      root.hidden = false;
      showWriteButton();
      start(); // load the list behind the modal
      openModal();
      if (modal.querySelector('[data-apr-thankyou]')) {
        try { root.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) { root.scrollIntoView(); }
      }
    }
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-apr-root]');
    for (var i = 0; i < roots.length; i++) setup(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme editor: re-init the (re)loaded section.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
